import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FirewallGeraet, FirewallRegel, FirewallVdom } from '../../services/firewallService';
import {
  DEFAULT_DRAWER_FILTERS,
  DrawerFilters,
  drawerGroups,
  filterDrawerRules,
  FlatVdom,
  isAnyRule,
  RATING_TITLE,
  RATING_UI_COLOR,
  ratingState,
  riskCount,
  swipePositions,
  windowRange,
} from '../../utils/firewallReview';
import { MONO, useSwipePalette } from './firewallSwipeTheme';
import { ruleTitle } from './FirewallRuleCard';

export const DRAWER_ROW_HEIGHT = 56;
const HEADER_ROW_HEIGHT = 32;
export const DRAWER_WIDTH = 440;

type ListItem =
  | { kind: 'header'; key: string; label: string; count: number }
  | { kind: 'rule'; key: string; rule: FirewallRegel; flatIndex: number; position: number };

export interface FirewallRuleDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Flache Liste „Firewall › VDOM“ in Anzeige-Reihenfolge (siehe `flattenGeraete`). */
  flat: FlatVdom<FirewallGeraet, FirewallVdom>[];
  currentIndex: number;
  currentRuleId: number | null;
  mode: 'swipe' | 'final';
  onSelect: (flatIndex: number, ruleId: number) => void;
}

/** Seitliche Regelliste: virtualisiert, mit Suche und Filtern; springt per Klick zu einer Regel. */
const FirewallRuleDrawer: React.FC<FirewallRuleDrawerProps> = ({
  open,
  onClose,
  flat,
  currentIndex,
  currentRuleId,
  mode,
  onSelect,
}) => {
  const palette = useSwipePalette();
  const [filters, setFilters] = useState<DrawerFilters>(DEFAULT_DRAWER_FILTERS);
  const [alleVdoms, setAlleVdoms] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);

  const abschnitte = useMemo(() => {
    const seen = new Set<string>();
    flat.forEach((entry) => entry.block.regeln.forEach((rule) => seen.add(rule.abschnitt)));
    return Array.from(seen);
  }, [flat]);

  // Gruppen „Firewall › VDOM“; bei nur einer Firewall steht nur der VDOM-Name im Kopf.
  const groups = useMemo(() => drawerGroups(flat), [flat]);

  const items = useMemo<ListItem[]>(() => {
    const result: ListItem[] = [];
    const shown = alleVdoms
      ? groups
      : groups[currentIndex]
        ? [groups[currentIndex]]
        : [];
    shown.forEach((group) => {
      const positionOf = swipePositions(group.block.regeln);
      const filtered = filterDrawerRules(group.block.regeln, filters, mode);
      if (alleVdoms) result.push({ kind: 'header', key: `h-${group.key}`, label: group.label, count: filtered.length });
      filtered.forEach((rule) => result.push({
        kind: 'rule',
        key: `r-${rule.id}`,
        rule,
        flatIndex: group.flatIndex,
        position: positionOf.get(rule.id) ?? 0,
      }));
    });
    return result;
  }, [groups, currentIndex, alleVdoms, filters, mode]);

  // Zeilen fester Höhe; Gruppenköpfe sind niedriger, deshalb Offsets vorab berechnen.
  const offsets = useMemo(() => {
    const result: number[] = new Array(items.length + 1);
    let top = 0;
    items.forEach((item, index) => {
      result[index] = top;
      top += item.kind === 'header' ? HEADER_ROW_HEIGHT : DRAWER_ROW_HEIGHT;
    });
    result[items.length] = top;
    return result;
  }, [items]);
  const totalHeight = offsets[items.length] ?? 0;
  const ruleCount = items.filter((item) => item.kind === 'rule').length;
  const totalRules = alleVdoms
    ? flat.reduce((sum, entry) => sum + entry.block.regeln.length, 0)
    : flat[currentIndex]?.block.regeln.length ?? 0;

  const measure = useCallback(() => {
    const node = listRef.current;
    if (!node) return 0;
    const height = node.clientHeight;
    if (height > 0) setViewport(height);
    return height;
  }, []);

  const scrollToCurrentRule = useCallback(() => {
    const node = listRef.current;
    if (!node || currentRuleId == null) return;
    const index = items.findIndex((item) => item.kind === 'rule' && item.rule.id === currentRuleId);
    if (index < 0) return;
    const vh = node.clientHeight;
    const target = Math.max(0, offsets[index] - Math.max(0, (vh - DRAWER_ROW_HEIGHT) / 2));
    node.scrollTop = target;
    setScrollTop(target);
    if (vh > 0) setViewport(vh);
  }, [currentRuleId, items, offsets]);

  useLayoutEffect(() => {
    if (!open) {
      setScrollTop(0);
      setViewport(0);
      return undefined;
    }
    measure();
    scrollToCurrentRule();
    const node = listRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, measure, scrollToCurrentRule]);

  // Nach Drawer-Animation und erstem Layout nochmals messen (sonst clientHeight 0 → leere Liste).
  useLayoutEffect(() => {
    if (!open) return undefined;
    let frame = 0;
    const run = () => {
      const height = measure();
      scrollToCurrentRule();
      if (height === 0 && frame < 8) {
        frame += 1;
        requestAnimationFrame(run);
      }
    };
    requestAnimationFrame(run);
    return undefined;
  }, [open, items.length, currentRuleId, measure, scrollToCurrentRule]);

  const listViewport = viewport > 0 ? viewport : DRAWER_ROW_HEIGHT * 12;
  const range = useMemo(() => windowRange(offsets, scrollTop, listViewport, 6), [offsets, scrollTop, listViewport]);

  const visible = items.slice(range.start, range.end);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        'data-testid': 'firewall-rule-drawer',
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          display: 'flex',
          flexDirection: 'column',
          bgcolor: palette.card,
          color: palette.text,
          borderLeft: `1px solid ${palette.cardBorder}`,
          backgroundImage: 'none',
        },
      } as object}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>Alle Regeln</Typography>
          <Typography data-testid="firewall-drawer-count" sx={{ fontSize: '0.78rem', color: palette.muted }}>
            {ruleCount} von {totalRules}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Regelliste schließen" sx={{ color: palette.muted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            data-testid="firewall-drawer-search"
            size="small"
            fullWidth
            autoFocus
            placeholder="Name, ID, Objekt, IP oder Port"
            value={filters.suche}
            onChange={(event) => setFilters((current) => ({ ...current, suche: event.target.value }))}
            onKeyDown={(event) => { if (event.key === 'Escape') onClose(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: palette.muted }} />
                </InputAdornment>
              ),
              endAdornment: filters.suche ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Suche leeren" onClick={() => setFilters((current) => ({ ...current, suche: '' }))}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <Box sx={{ px: 2, pb: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
          <FormControl size="small">
            <InputLabel id="fw-drawer-abschnitt">Abschnitt</InputLabel>
            <Select
              labelId="fw-drawer-abschnitt"
              label="Abschnitt"
              data-testid="firewall-drawer-filter-abschnitt"
              value={filters.abschnitt}
              onChange={(event) => setFilters((current) => ({ ...current, abschnitt: String(event.target.value) }))}
            >
              <MenuItem value="alle">Alle</MenuItem>
              {abschnitte.map((abschnitt) => (
                <MenuItem key={abschnitt} value={abschnitt}>{abschnitt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="fw-drawer-bewertung">Bewertung</InputLabel>
            <Select
              labelId="fw-drawer-bewertung"
              label="Bewertung"
              data-testid="firewall-drawer-filter-bewertung"
              value={filters.bewertung}
              onChange={(event) => setFilters((current) => ({ ...current, bewertung: event.target.value as DrawerFilters['bewertung'] }))}
            >
              <MenuItem value="alle">Alle</MenuItem>
              <MenuItem value="offen">Unbewertet</MenuItem>
              <MenuItem value="rot">Kritisch (rot)</MenuItem>
              <MenuItem value="gelb">Prüfen (gelb)</MenuItem>
              <MenuItem value="gruen">Unbedenklich (grün)</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="fw-drawer-risiko">Risiko</InputLabel>
            <Select
              labelId="fw-drawer-risiko"
              label="Risiko"
              data-testid="firewall-drawer-filter-risiko"
              value={filters.risiko}
              onChange={(event) => setFilters((current) => ({ ...current, risiko: event.target.value as DrawerFilters['risiko'] }))}
            >
              <MenuItem value="alle">Alle</MenuItem>
              <MenuItem value="any">Any-Regeln</MenuItem>
              <MenuItem value="risiko">Mit Prüfhinweis</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            size="small"
            checked={alleVdoms}
            onChange={(event) => setAlleVdoms(event.target.checked)}
            inputProps={{ 'aria-label': 'Alle VDOMs anzeigen' } as object}
            data-testid="firewall-drawer-all-vdoms"
          />
          <Typography sx={{ fontSize: '0.8rem', color: palette.muted }}>
            {new Set(flat.map((entry) => entry.geraet.id)).size > 1 ? 'Alle Firewalls und VDOMs gruppiert' : 'Alle VDOMs gruppiert'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {(filters.suche || filters.abschnitt !== 'alle' || filters.bewertung !== 'alle' || filters.risiko !== 'alle') && (
            <Chip
              size="small"
              label="Filter zurücksetzen"
              onClick={() => setFilters(DEFAULT_DRAWER_FILTERS)}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <Box
          ref={listRef}
          data-testid="firewall-drawer-list"
          role="listbox"
          aria-label="Regeln"
          onScroll={(event) => setScrollTop((event.target as HTMLDivElement).scrollTop)}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', borderTop: `1px solid ${palette.cardBorder}`, position: 'relative' }}
        >
          {items.length === 0 && (
            <Typography sx={{ p: 2, color: palette.muted, fontSize: '0.85rem' }}>Keine Regel passt zu Suche und Filtern.</Typography>
          )}
          <Box sx={{ height: totalHeight, position: 'relative' }}>
            {visible.map((item, offsetIndex) => {
              const index = range.start + offsetIndex;
              const top = offsets[index];
              if (item.kind === 'header') {
                return (
                  <Box
                    key={item.key}
                    data-testid="firewall-drawer-group"
                    sx={{
                      position: 'absolute',
                      top,
                      left: 0,
                      right: 0,
                      height: HEADER_ROW_HEIGHT,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: palette.tile,
                      borderBottom: `1px solid ${palette.tileBorder}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: palette.muted }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: palette.faint }}>{item.count}</Typography>
                  </Box>
                );
              }
              const { rule } = item;
              const state = ratingState(rule, mode);
              const current = rule.id === currentRuleId;
              const risks = riskCount(rule);
              const anyRule = isAnyRule(rule);
              return (
                <Box
                  key={item.key}
                  role="option"
                  aria-selected={current}
                  data-testid="firewall-drawer-row"
                  data-rule-id={rule.id}
                  data-position={item.position}
                  data-current={current ? 'true' : undefined}
                  tabIndex={0}
                  onClick={() => onSelect(item.flatIndex, rule.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(item.flatIndex, rule.id);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top,
                    left: 0,
                    right: 0,
                    height: DRAWER_ROW_HEIGHT,
                    boxSizing: 'border-box',
                    display: 'grid',
                    gridTemplateColumns: '56px minmax(0, 1fr) auto',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    cursor: 'pointer',
                    borderLeft: `3px solid ${current ? palette.green : 'transparent'}`,
                    bgcolor: current ? palette.greenBg : 'transparent',
                    borderBottom: `1px solid ${palette.tileBorder}`,
                    '&:hover': { bgcolor: current ? palette.greenBg : palette.tileHover },
                    '&:focus-visible': { outline: `2px solid ${palette.green}`, outlineOffset: -2 },
                  }}
                >
                  <Typography
                    data-testid="firewall-drawer-position"
                    sx={{ fontFamily: MONO, fontSize: '0.78rem', color: palette.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {item.position}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: palette.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ruleTitle(rule)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: palette.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {rule.abschnitt}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {anyRule && (
                      <Chip size="small" label="Any" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, color: '#fff', bgcolor: palette.red, '& .MuiChip-label': { px: 0.6 } }} />
                    )}
                    {risks > 0 && (
                      <Tooltip title={(rule.risiken ?? []).map((risk) => risk.label).join(' · ')} arrow>
                        <Box
                          component="span"
                          data-testid="firewall-drawer-risk"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.7rem', fontWeight: 700, color: palette.amber }}
                        >
                          <WarningAmberIcon sx={{ fontSize: 14 }} />
                          {risks}
                        </Box>
                      </Tooltip>
                    )}
                    <Tooltip title={state === 'offen' ? 'Noch nicht bewertet' : RATING_TITLE[state]} arrow>
                      <Box
                        component="span"
                        data-testid="firewall-drawer-state"
                        data-state={state}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: state === 'offen' ? 'transparent' : RATING_UI_COLOR[state],
                          border: state === 'offen' ? `2px dashed ${palette.faint}` : 'none',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FirewallRuleDrawer;
